import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Formik, useFormikContext } from 'formik';
import {
  getGetFeatureConfigsPublicQueryKey,
  getGetFeatureConfigsQueryKey,
  useCreateFeatureConfig,
  useUpdateSiteFeatureConfig,
} from '@juno/client-api';
import { FeatureConfig as FeatureConfigModel, FeatureConfigTypeEnum } from '@juno/client-api/model';
import { FeatureConfig as FeatureConfigUtils } from '@juno/client-api/utils';
import { ASPECT_RATIOS, JunoImageUpload } from '@juno/ui';
import { MutationAction, onMutation, optimizeImage } from '@juno/utils';
import ColorPicker from '../PageBuilderPanel/ColorPicker';
import { StyledFormControl, StyledImageInputBox } from './styles';

interface FormValues {
  logo: string;
  favicon: string;
  loginBgColor: string;
}

interface HeaderEditFormInnerProps {
  setUnsavedChanges?: (value: boolean) => void;
  isSaving: boolean;
}

const HeaderEditFormInner = ({ setUnsavedChanges, isSaving }: HeaderEditFormInnerProps) => {
  const { values, dirty, setFieldValue, isValid, handleSubmit } = useFormikContext<FormValues>();

  useEffect(() => {
    setUnsavedChanges?.(dirty);
  }, [dirty, setUnsavedChanges]);

  return (
    <Box minWidth={280}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant='subtitle1'>Logo</Typography>
          <Typography variant='body2'>
            Your logo will be displayed in the top left side of the header. If it appears too small
            or large, try cropping the image first.
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign='right'>
          <StyledImageInputBox>
            <StyledFormControl>
              <Box sx={{ width: 140 }}>
                <JunoImageUpload
                  style={{ aspectRatio: '2/1', height: 70 }}
                  src={optimizeImage(140, values.logo ? values.logo : undefined)}
                  srcUrl={values.logo ? values.logo : undefined}
                  onFileUploaded={(selected) => {
                    setFieldValue('logo', selected?.url || null);
                  }}
                  iconInsteadOfText={true}
                  hoverForControls={true}
                  aspectRatios={Object.values(ASPECT_RATIOS)}
                  imageType='Logo'
                />
              </Box>
            </StyledFormControl>
          </StyledImageInputBox>
        </Grid>
        <Grid item xs={6}>
          <Typography variant='subtitle1'>Favicon</Typography>
          <Typography variant='body2'>
            The favicon displays on the browser tab and is usually a small square image.
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign='right'>
          <StyledImageInputBox>
            <StyledFormControl>
              <Box>
                <JunoImageUpload
                  style={{ aspectRatio: '1/1', height: 70, width: 70 }}
                  src={optimizeImage(140, values.favicon ? values.favicon : undefined)}
                  srcUrl={values.favicon ? values.favicon : undefined}
                  onFileUploaded={(selected) => {
                    setFieldValue('favicon', selected?.url || null);
                  }}
                  iconInsteadOfText={true}
                  hoverForControls={true}
                  aspectRatios={[ASPECT_RATIOS.ONE_ONE]}
                  imageType='Favicon'
                />
              </Box>
            </StyledFormControl>
          </StyledImageInputBox>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant='subtitle1'>Login Page Background Color</Typography>
          <Typography variant='body2'>
            The background color displays behind the login modal on the login page.
          </Typography>
        </Grid>
        <Grid item xs={6} py={2} justifyContent={'flex-end'} display={'flex'}>
          <ColorPicker
            label='LoginBackground'
            color={values.loginBgColor}
            colorName='login.background'
            handleChange={(color) => setFieldValue('loginBgColor', color)}
            disabled={isSaving}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} textAlign='right'>
        <LoadingButton
          aria-label='Save Site Branding'
          variant='contained'
          onClick={() => handleSubmit()}
          loading={isSaving}
          disabled={isSaving || !dirty || !isValid}
          sx={{ marginLeft: 'auto' }}
        >
          Save
        </LoadingButton>
      </Grid>
    </Box>
  );
};

interface ModuleEditFormProps {
  siteId: string;
  configs: FeatureConfigModel[];
  setUnsavedChanges?: (unsavedChanges: boolean) => void;
}

const HeaderEditForm = ({ siteId, configs, setUnsavedChanges }: ModuleEditFormProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const themeConfig = FeatureConfigUtils.getThemeConfig(configs);

  const initialValues: FormValues = {
    logo: themeConfig?.config?.logo as string,
    favicon: themeConfig?.config?.favicon as string,
    loginBgColor: (themeConfig?.config?.loginBgColor as string) || '#e7edf2',
  };

  const queryClient = useQueryClient();
  const refetchFeatures = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetFeatureConfigsPublicQueryKey(siteId) });
    await queryClient.invalidateQueries({ queryKey: getGetFeatureConfigsQueryKey(siteId) });
  };

  const updateConfig = useUpdateSiteFeatureConfig(
    onMutation(MutationAction.UPDATE, 'FeatureConfig', refetchFeatures),
  );
  const createFeatureConfig = useCreateFeatureConfig(
    onMutation(MutationAction.CREATE, 'FeatureConfig', refetchFeatures),
  );

  const submitBrandingConfig = async (values: FormValues) => {
    setIsSaving(true);

    try {
      const updatedConfig = {
        ...themeConfig?.config,
        logo: values.logo,
        favicon: values.favicon,
        loginBgColor: values.loginBgColor,
      };

      const updatedThemeConfig = { ...themeConfig, config: updatedConfig };

      if (updatedThemeConfig.id) {
        await updateConfig.mutateAsync({
          siteId,
          configType: themeConfig?.type || FeatureConfigTypeEnum.theme,
          data: { ...updatedThemeConfig, type: themeConfig?.type || FeatureConfigTypeEnum.theme },
        });
      } else {
        await createFeatureConfig.mutateAsync({
          siteId,
          data: { ...updatedThemeConfig, type: FeatureConfigTypeEnum.theme },
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submitBrandingConfig} enableReinitialize>
      <HeaderEditFormInner setUnsavedChanges={setUnsavedChanges} isSaving={isSaving} />
    </Formik>
  );
};

export default HeaderEditForm;
